import { useEffect, useMemo, useRef } from "react";

import { COLS, moonHtml, START, STEP } from "@/lib/asciiMoon";

// DESIGN.md component: ascii-stage. The moon turns one frame every
// x-zsd.motion.ascii-frame. Decorative: hidden from assistive technology.
// With reduced motion it shows one still frame. It stops while off-screen or
// in a background tab.

function frameMs(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--zsd-x-zsd-motion-ascii-frame");
  return parseFloat(raw) || 83;
}

export function AsciiMoon({ className }: { className?: string }) {
  const pre = useRef<HTMLPreElement>(null);
  const still = useMemo(() => moonHtml(START, 0), []);

  useEffect(() => {
    const el = pre.current;
    if (!el) return;
    const reduce = matchMedia("(prefers-reduced-motion: reduce)");
    let angle = START;
    let tick = 0;
    let timer: ReturnType<typeof setInterval> | undefined;
    let onScreen = false;

    const sync = () => {
      const run = onScreen && !reduce.matches && document.visibilityState === "visible";
      if (run && !timer) {
        timer = setInterval(() => {
          angle += STEP;
          tick += 1;
          el.innerHTML = moonHtml(angle, tick);
        }, frameMs());
      } else if (!run && timer) {
        clearInterval(timer);
        timer = undefined;
      }
      if (reduce.matches) el.innerHTML = still;
    };

    const io = new IntersectionObserver(([entry]) => {
      onScreen = Boolean(entry?.isIntersecting);
      sync();
    });
    io.observe(el);
    reduce.addEventListener("change", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      clearInterval(timer);
      io.disconnect();
      reduce.removeEventListener("change", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [still]);

  return (
    <div className={className} aria-hidden="true">
      <div className="ascii-fit">
        <pre
          ref={pre}
          className="ascii-stage"
          style={{ "--ascii-cols": COLS } as React.CSSProperties}
          dangerouslySetInnerHTML={{ __html: still }}
        />
      </div>
    </div>
  );
}
